import { computed, Ref, ref, shallowRef, watch } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { graphql, FragmentType } from '@/gql'
import { CustomFieldTypeValueInput, TypeSupportingCustomFieldValues } from '@/gql/graphql'
import { useTranslation } from '@/composables/translation'
import { FieldableFormCustomFieldsQuery } from '@/components/Fieldable/graphql/queries'
import {
  FieldableFormCustomFieldFragment,
  FieldableFormCustomFieldValueFragment,
} from '@/components/Fieldable/graphql/fragments'
import {
  diffCustomFieldValueInputs,
  hasFieldValue,
  mapCustomFieldValueToCustomFieldValueInput,
} from '@/components/Fieldable/utils'
import { isCustomFieldRequired } from '@/components/Fields/utils/CustomFieldUtils'
import useFormCustomFieldValues from '@/components/Fieldable/composables/useFormCustomFieldValues'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'

/**
 * Composable for profile modal custom fields: query, local state, validation, and save.
 * Used by RecruiterProfileModal and WorkerProfileModal.
 *
 * `subjectAccount` is the account whose field values are being edited (the
 * worker in the SA-on-behalf flow). Falls back to the current account for
 * the worker's / recruiter's own-profile views, where viewer and subject
 * are the same. The backend `FieldValuePolicy::manage` routes on this
 * account, so passing the SA in the on-behalf case makes a worker-visibility
 * field look unmanageable (recruiter_input_allowed=false) and silently drops
 * values from the save.
 */
export default function useProfileModalCustomFields(
  companyId: string,
  clientId: string,
  appliesTo: TypeSupportingCustomFieldValues,
  enabled: Ref<boolean>,
  initialCustomFieldValues: Ref<FragmentType<typeof FieldableFormCustomFieldValueFragment>[]>,
  subjectAccount?: string,
) {
  const { $t } = useTranslation()
  const saving = ref(false)

  const viewerAccount = subjectAccount ?? currentAccount.globalId

  const { result: fieldsResult, loading: fieldsLoading, error: fieldsError } = useQuery(
    FieldableFormCustomFieldsQuery,
    () => ({
      viewerAccount,
      accounts: [companyId],
      appliesTo: [appliesTo],
      first: PER_PAGE_MAXIMUM,
    }),
    {
      clientId: 'publicApi',
      enabled,
    },
  )

  const fields = computed(
    () => (fieldsResult.value?.customFields?.data ?? []) as FragmentType<
      typeof FieldableFormCustomFieldFragment
    >[],
  )

  const hasCustomFields = computed(
    () => !fieldsLoading.value && (fields.value?.length ?? 0) > 0,
  )
  const showCustomFieldsSection = computed(
    () => fieldsLoading.value || (hasCustomFields.value ?? false),
  )

  const initialFieldValues = computed(() =>
    mapCustomFieldValueToCustomFieldValueInput(initialCustomFieldValues.value),
  )

  const localFieldValues = ref<CustomFieldTypeValueInput[]>([])
  const savedFieldValues = ref<CustomFieldTypeValueInput[]>([])

  const hasUnsavedChanges = computed(
    () => diffCustomFieldValueInputs(localFieldValues.value, savedFieldValues.value).length > 0,
  )

  watch(
    initialFieldValues,
    (newValues) => {
      localFieldValues.value = [...newValues]
      savedFieldValues.value = [...newValues]
    },
    { immediate: true },
  )

  const fieldsRef = computed(() => fields.value ?? [])
  const initialValueFragmentsRef = computed(() => initialCustomFieldValues.value ?? [])
  const { mappedAndSortedFields: savedMappedFields } = useFormCustomFieldValues(
    fieldsRef as Ref<FragmentType<typeof FieldableFormCustomFieldFragment>[]>,
    savedFieldValues,
    initialValueFragmentsRef,
  )
  const { mappedAndSortedFields: localMappedFields } = useFormCustomFieldValues(
    fieldsRef as Ref<FragmentType<typeof FieldableFormCustomFieldFragment>[]>,
    localFieldValues,
    initialValueFragmentsRef,
  )

  const customFieldsCompleted = computed(() => {
    if (fieldsLoading.value || (fields.value?.length ?? 0) === 0) return true
    return savedMappedFields.value.every((mapped) => {
      if (!isCustomFieldRequired(mapped.fieldTyped.settings)) return true
      return hasFieldValue(mapped.fieldValue)
    })
  })

  const fieldErrors = shallowRef<Record<string, Ref<string>>>({})

  const validateRequiredFields = (): boolean => {
    const errors: Record<string, Ref<string>> = {}
    for (const mapped of localMappedFields.value) {
      // A locked field is never submitted, so it cannot block the save.
      if (
        mapped.fieldTyped.viewerCanManageFieldValues &&
        isCustomFieldRequired(mapped.fieldTyped.settings) &&
        !hasFieldValue(mapped.fieldValue)
      ) {
        errors[mapped.fieldTyped.id] = ref($t('This field is required.'))
      }
    }
    fieldErrors.value = errors
    return Object.keys(errors).length === 0
  }

  const profileModalUpdateMutation = graphql(`
    mutation ProfileModalUpdateCustomFieldValues(
      $input: UpdateWorkerCustomFieldValuesInput!
    ) {
      updateWorkerCustomFieldValues(input: $input) {
        # Returning the entity lets Apollo attach newly created values to its cached list.
        fieldable {
          ... on CompanySupplier {
            id
            customFieldValues {
              ...FieldableFormCustomFieldValue
            }
          }
        }
        customFieldValues {
          ...FieldableFormCustomFieldValue
        }
      }
    }
  `)

  const { mutate } = useMutation(profileModalUpdateMutation, {
    clientId: 'publicApi',
  })

  const editableFieldIds = computed(() =>
    new Set(
      localMappedFields.value
        .filter((mapped) => mapped.fieldTyped.viewerCanManageFieldValues)
        .map((mapped) => mapped.fieldTyped.id),
    ),
  )

  const handleSave = async (): Promise<boolean> => {
    if (!validateRequiredFields()) return false

    // Only the values the user changed since the modal loaded (or since the
    // last save). Submitting untouched fields deletes their stored values
    // whenever the form holds them as empty.
    const changedValues = diffCustomFieldValueInputs(
      localFieldValues.value,
      savedFieldValues.value,
    )
    const editableValues = changedValues.filter((fv) => {
      const id =
        fv.singleSelect?.id ??
        fv.freeText?.id ??
        fv.multiSelect?.id ??
        fv.fileUpload?.id ??
        fv.date?.id ??
        fv.boolean?.id
      return id == null || editableFieldIds.value.has(id)
    })

    // The mutation's `values` field is required and it syncs everything it
    // receives, so when nothing changed, don't call it at all.
    if (editableValues.length === 0) {
      return true
    }

    saving.value = true
    try {
      await mutate({
        input: {
          appliesTo: clientId,
          values: editableValues,
        },
      })
      savedFieldValues.value = [...localFieldValues.value]
      return true
    } catch {
      return false
    } finally {
      saving.value = false
    }
  }

  return {
    fields,
    fieldsLoading,
    fieldsError,
    hasCustomFields,
    showCustomFieldsSection,
    localFieldValues,
    hasUnsavedChanges,
    initialFieldValueFragments: initialValueFragmentsRef,
    fieldErrors,
    customFieldsCompleted,
    saving,
    handleSave,
    validateRequiredFields,
  }
}
